import React, { useMemo, useState } from 'react';

import { Tooltip } from 'antd';
import { AlertTriangle, CloudRain, Radio } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  KPIDetailDrawer,
  type KpiDetailKind,
} from '@/features/dashboard/components/KPIDetailDrawer';
import { WeatherKpiCard } from '@/features/dashboard/components/WeatherKpiCard';
import { useDashboardIoTStations } from '@/features/realtime/hooks/useDashboardIoTStations';
import {
  maxRainfallFromStation,
  stationHasRainfallChannel,
  unitForRoutingKey,
  RAINFALL_ROUTING_KEY,
} from '@/features/realtime/utils/formatStationChannelReadings';
import { useAppSelector } from '@/hooks/useAppSelector';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

/** Shared shell for KPI cards and skeletons — keep height/layout aligned with map overlay constants. */
const KPI_CARD_SHELL_STYLE: React.CSSProperties = {
  background: 'var(--map-chrome-panel)',
  backdropFilter: 'blur(16px)',
  border: '1px solid var(--glass-border)',
  borderRadius: 12,
  padding: '10px 12px',
  flex: '1 1 140px',
  minWidth: 120,
  maxWidth: 220,
  boxSizing: 'border-box',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  transition: 'all 0.3s ease',
  cursor: 'default',
  pointerEvents: 'auto',
};

const KPI_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  maxWidth: '100%',
  padding: '0 4px',
  boxSizing: 'border-box',
};

interface KPICardProps {
  title: string;
  value: string | number;
  unit?: string;
  /** Ghi đè style cho giá trị chính (vd. câu thông báo thay cho số). */
  valueStyle?: React.CSSProperties;
  icon: React.ReactNode;
  color: string;
  /** Giải thích khi hover tiêu đề (ví dụ KPI không khớp timeline). */
  titleTooltip?: string;
  /** Dòng phụ dưới giá trị (ví dụ vùng có mức max). */
  footer?: React.ReactNode;
  /** Nhấn để thực hiện hành động (vd. bay bản đồ tới vùng max depth). */
  onCardClick?: () => void;
  /** `aria-label` khi thẻ có `onCardClick`. */
  cardActionAriaLabel?: string;
  /** Dòng bổ sung dưới footer (vd. đỉnh lưới theo vùng đang chọn). */
  subFooter?: React.ReactNode;
  /** Giải thích khi hover dòng subFooter. */
  subFooterTooltip?: string;
  /** Tỷ lệ phần trăm cho thanh tiến độ (0-100). */
  progress?: number;
  /** Bật hiệu ứng nhấp nháy cho icon. */
  statusPulse?: boolean;
  /** Bật hiệu ứng phát sáng cho viền thẻ. */
  glow?: boolean;
}

function KPICard({
  title,
  value,
  unit,
  valueStyle,
  icon,
  color,
  titleTooltip,
  footer,
  subFooter,
  subFooterTooltip,
  onCardClick,
  cardActionAriaLabel,
  progress,
  statusPulse,
  glow,
}: KPICardProps) {
  const titleEl = (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        lineHeight: 1.35,
      }}
    >
      {title}
    </span>
  );

  const interactive = Boolean(onCardClick);

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? cardActionAriaLabel : undefined}
      style={{
        ...KPI_CARD_SHELL_STYLE,
        cursor: interactive ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        borderLeft: glow ? `2px solid ${color}cc` : undefined,
      }}
      onClick={interactive ? onCardClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCardClick?.();
              }
            }
          : undefined
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.borderColor = `${color}88`;
        e.currentTarget.style.boxShadow = `0 12px 40px ${color}25`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'var(--glass-border)';
        e.currentTarget.style.boxShadow = glow ? '' : 'var(--shadow-lg)';
      }}
    >
      {/* Subtle corner accent */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 40,
          height: 40,
          background: `radial-gradient(circle at top right, ${color}18 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, paddingRight: 2 }}>
          {titleTooltip ? <Tooltip title={titleTooltip}>{titleEl}</Tooltip> : titleEl}
        </span>
        <div
          style={{
            width: 24,
            height: 24,
            flexShrink: 0,
            borderRadius: 6,
            background: `${color}15`,
            border: `1px solid ${color}33`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className={statusPulse ? 'kpi-animate-pulse' : ''}
        >
          {React.cloneElement(icon as React.ReactElement, { size: 12 })}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 18,
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: '-0.3px',
            ...valueStyle,
          }}
        >
          {value}
        </span>
        {unit && (
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>{unit}</span>
        )}
      </div>
      {footer ? (
        <div
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: 'var(--text-muted)',
            lineHeight: 1.35,
            marginTop: -2,
          }}
        >
          {footer}
        </div>
      ) : null}

      {progress !== undefined && (
        <div className="kpi-progress-container">
          <div
            className="kpi-progress-fill"
            style={{
              width: `${Math.min(100, Math.max(0, progress))}%`,
              background: color,
              boxShadow: `0 0 8px ${color}88`,
            }}
          />
        </div>
      )}

      {subFooter ? (
        subFooterTooltip ? (
          <Tooltip title={subFooterTooltip}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 500,
                color: 'rgba(148, 163, 184, 0.95)',
                lineHeight: 1.35,
                marginTop: 2,
                cursor: 'help',
              }}
            >
              {subFooter}
            </div>
          </Tooltip>
        ) : (
          <div
            style={{
              fontSize: 9,
              fontWeight: 500,
              color: 'rgba(148, 163, 184, 0.95)',
              lineHeight: 1.35,
              marginTop: 2,
            }}
          >
            {subFooter}
          </div>
        )
      ) : null}
    </div>
  );
}

function KPICardSkeleton() {
  return (
    <div
      style={{
        ...KPI_CARD_SHELL_STYLE,
        pointerEvents: 'none',
        cursor: 'default',
      }}
      aria-hidden
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          className="skeleton"
          style={{ height: 11, width: '65%', maxWidth: 120, borderRadius: 4 }}
        />
        <div
          className="skeleton"
          style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0 }}
        />
      </div>
      <div
        className="skeleton"
        style={{ height: 18, width: '45%', maxWidth: 72, borderRadius: 4 }}
      />
    </div>
  );
}

export function KPIOverlay() {
  const user = useAppSelector((s) => s.auth.user);
  const isLiveScenario = useAppSelector((s) => s.scenario.mode === 'live');
  const operationalApi = shouldFetchOperationalApi();
  const showWeatherKpi = Boolean(user && operationalApi && isLiveScenario);
  const { stations, isLoading, stationsForbidden } = useDashboardIoTStations({
    skip: !user || !operationalApi,
  });
  const { t } = useTranslation();
  const [kpiDetail, setKpiDetail] = useState<KpiDetailKind | null>(null);

  const kpi = useMemo(() => {
    const total = stations.length;
    const online = stations.filter((s) => s.status === 'online').length;
    const breached = stations.filter((s) => s.thresholdBreached).length;

    const rainStations = stations.filter(stationHasRainfallChannel);
    let maxRain: { value: number; name: string; unit: string } | null = null;
    for (const s of rainStations) {
      const v = maxRainfallFromStation(s);
      if (v == null || !Number.isFinite(v)) continue;
      if (!maxRain || v > maxRain.value) {
        maxRain = {
          value: v,
          name: (s.name ?? s.id).trim() || s.id,
          unit: unitForRoutingKey(RAINFALL_ROUTING_KEY),
        };
      }
    }

    const rainfallMaxIsZero = rainStations.length > 0 && maxRain != null && maxRain.value <= 0;

    return { total, online, breached, maxRain, rainfallMaxIsZero };
  }, [stations]);

  const loading = Boolean(user && operationalApi) && isLoading;

  return (
    <>
      <div style={KPI_ROW_STYLE} aria-busy={loading} aria-live="polite">
        {loading ? (
          <>
            {showWeatherKpi ? <KPICardSkeleton /> : null}
            <KPICardSkeleton />
            <KPICardSkeleton />
            <KPICardSkeleton />
          </>
        ) : (
          <>
            {showWeatherKpi ? <WeatherKpiCard /> : null}
            <KPICard
              title={t('kpi.iotStationsOnline')}
              value={kpi.total === 0 ? '—' : kpi.online}
              unit={kpi.total === 0 ? undefined : `/ ${kpi.total}`}
              icon={<Radio size={14} color="#06b6d4" />}
              color="#06b6d4"
              titleTooltip={t('kpi.iotStationsOnlineHint')}
              footer={
                stationsForbidden
                  ? t('dashboard.stationsForbidden')
                  : kpi.total === 0
                    ? t('kpi.iotStationsEmpty')
                    : undefined
              }
              onCardClick={() => setKpiDetail('iot')}
              cardActionAriaLabel={t('kpi.detailOpenIot')}
              progress={kpi.total > 0 ? (kpi.online / kpi.total) * 100 : undefined}
              statusPulse={kpi.online > 0}
            />
            <KPICard
              title={t('kpi.thresholdBreached')}
              value={kpi.total === 0 ? '—' : kpi.breached}
              icon={<AlertTriangle size={14} color="#f97316" />}
              color="#f97316"
              titleTooltip={t('kpi.thresholdBreachedHint')}
              footer={
                kpi.breached === 0
                  ? t('kpi.thresholdBreachedNone')
                  : t('kpi.thresholdBreachedFooter', { count: kpi.breached })
              }
              onCardClick={() => setKpiDetail('alerts')}
              cardActionAriaLabel={t('kpi.detailOpenAlerts')}
              glow={kpi.breached > 0}
              statusPulse={kpi.breached > 0}
            />
            <KPICard
              title={t('kpi.rainfallMaxReading')}
              value={
                !kpi.maxRain
                  ? '—'
                  : kpi.rainfallMaxIsZero
                    ? t('kpi.rainfallNoneRecorded')
                    : kpi.maxRain.value.toFixed(1)
              }
              unit={kpi.maxRain && !kpi.rainfallMaxIsZero ? kpi.maxRain.unit : undefined}
              valueStyle={
                kpi.rainfallMaxIsZero
                  ? {
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      letterSpacing: 0,
                      textTransform: 'none',
                    }
                  : undefined
              }
              icon={<CloudRain size={14} color="#6366f1" />}
              color="#6366f1"
              titleTooltip={t('kpi.rainfallMaxReadingHint')}
              footer={
                !kpi.maxRain
                  ? t('kpi.rainfallNoStations')
                  : kpi.rainfallMaxIsZero
                    ? undefined
                    : t('kpi.rainfallMaxAtStation', { station: kpi.maxRain.name })
              }
              onCardClick={() => setKpiDetail('rain')}
              cardActionAriaLabel={t('kpi.detailOpenRain')}
            />
          </>
        )}
      </div>
      {kpiDetail != null ? (
        <KPIDetailDrawer
          key={kpiDetail}
          open
          kind={kpiDetail}
          onClose={() => setKpiDetail(null)}
          stations={stations}
          maxRain={kpi.maxRain}
          rainfallMaxIsZero={kpi.rainfallMaxIsZero}
        />
      ) : null}
    </>
  );
}
